'use client';

import { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Check, Search, X } from 'lucide-react';
import { Field } from './wizard-ui';
import {
  VENUE_COUNTRIES,
  citiesForState,
  statesForCountry,
} from './venue-location-data';

type VenueLocationFields = {
  venue_country: string;
  venue_state: string;
  venue_city: string;
  venue_postal_code: string;
};

type VenueLocationSelectsProps = {
  values: VenueLocationFields;
  inputClass: string;
  cityError?: string;
  postalError?: string;
  onChange: (patch: Partial<VenueLocationFields>) => void;
};

export function VenueLocationSelects({
  values,
  inputClass,
  cityError,
  postalError,
  onChange,
}: VenueLocationSelectsProps) {
  // Dropdown open states
  const [countryOpen, setCountryOpen] = useState(false);
  const [stateOpen, setStateOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);

  // Search filter inputs
  const [countryQuery, setCountryQuery] = useState('');
  const [stateQuery, setStateQuery] = useState('');

  // Refs for outside click
  const countryRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef<HTMLDivElement>(null);
  const cityRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (countryRef.current && !countryRef.current.contains(e.target as Node)) {
        setCountryOpen(false);
      }
      if (stateRef.current && !stateRef.current.contains(e.target as Node)) {
        setStateOpen(false);
      }
      if (cityRef.current && !cityRef.current.contains(e.target as Node)) {
        setCityOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtered countries (alphabetical + searchable)
  const filteredCountries = useMemo(() => {
    const q = countryQuery.trim().toLowerCase();
    if (!q) return VENUE_COUNTRIES;
    return VENUE_COUNTRIES.filter((c) => c.name.toLowerCase().includes(q));
  }, [countryQuery]);

  // States for selected country (alphabetical + searchable)
  const availableStates = useMemo(() => {
    if (!values.venue_country.trim()) return [];
    return statesForCountry(values.venue_country);
  }, [values.venue_country]);

  const filteredStates = useMemo(() => {
    const q = stateQuery.trim().toLowerCase();
    if (!q) return availableStates;
    return availableStates.filter((s) => s.name.toLowerCase().includes(q));
  }, [availableStates, stateQuery]);

  // City suggestions based on country & state + typed city
  const citySuggestions = useMemo(() => {
    const country = values.venue_country.trim();
    const state = values.venue_state.trim();
    let candidates: string[] = [];

    if (country && state) {
      candidates = citiesForState(country, state);
    } else if (country) {
      // Collect all cities for this country across its states
      const allStates = statesForCountry(country);
      const set = new Set<string>();
      allStates.forEach((s) => s.cities.forEach((c) => set.add(c)));
      candidates = Array.from(set).sort();
    }

    const q = values.venue_city.trim().toLowerCase();
    if (!q) return candidates.slice(0, 15);
    return candidates.filter((c) => c.toLowerCase().includes(q)).slice(0, 20);
  }, [values.venue_country, values.venue_state, values.venue_city]);

  return (
    <>
      <div className="grid gap-6 sm:grid-cols-2">
        {/* Country: Searchable & Alphabetical Combobox */}
        <Field label="Country">
          <div ref={countryRef} className="relative">
            <button
              type="button"
              onClick={() => {
                setCountryOpen((prev) => !prev);
                setCountryQuery('');
              }}
              className={`${inputClass} flex items-center justify-between text-left cursor-pointer`}
              aria-haspopup="listbox"
              aria-expanded={countryOpen}
            >
              <span className={values.venue_country ? 'text-forest' : 'text-forest/40'}>
                {values.venue_country || 'Select or search country'}
              </span>
              <ChevronDown className="h-4 w-4 text-moss shrink-0 transition-transform duration-200" />
            </button>

            {countryOpen && (
              <div className="absolute z-30 mt-1 max-h-60 w-full overflow-hidden rounded-xl border border-nature-border bg-white shadow-xl">
                <div className="flex items-center border-b border-nature-border px-3 py-2 bg-plaster/50">
                  <Search className="h-4 w-4 text-moss shrink-0" />
                  <input
                    type="text"
                    value={countryQuery}
                    onChange={(e) => setCountryQuery(e.target.value)}
                    placeholder="Type to search country…"
                    className="w-full bg-transparent px-2 text-sm text-forest placeholder:text-forest/40 focus:outline-none"
                    autoFocus
                  />
                  {countryQuery && (
                    <button
                      type="button"
                      onClick={() => setCountryQuery('')}
                      className="text-moss hover:text-forest"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                <div className="max-h-48 overflow-y-auto p-1 text-sm">
                  {filteredCountries.map((c) => {
                    const isSelected = values.venue_country.toLowerCase() === c.name.toLowerCase();
                    return (
                      <button
                        key={c.name}
                        type="button"
                        onClick={() => {
                          onChange({
                            venue_country: c.name,
                            venue_state: '',
                            venue_city: '',
                          });
                          setCountryOpen(false);
                          setCountryQuery('');
                        }}
                        className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-plaster transition-colors ${
                          isSelected ? 'font-semibold text-moss bg-plaster/80' : 'text-forest'
                        }`}
                      >
                        <span>{c.name}</span>
                        {isSelected && <Check className="h-4 w-4 text-moss" />}
                      </button>
                    );
                  })}

                  {filteredCountries.length === 0 && (
                    <div className="p-3 text-center text-xs text-forest/60">
                      <p>No predefined country found for &ldquo;{countryQuery}&rdquo;.</p>
                      <button
                        type="button"
                        onClick={() => {
                          onChange({
                            venue_country: countryQuery.trim(),
                            venue_state: '',
                            venue_city: '',
                          });
                          setCountryOpen(false);
                          setCountryQuery('');
                        }}
                        className="mt-2 text-xs font-semibold text-moss underline"
                      >
                        Use &ldquo;{countryQuery.trim()}&rdquo; as custom country
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Field>

        {/* State/Region: Searchable Combobox */}
        <Field label="State / region">
          <div ref={stateRef} className="relative">
            <button
              type="button"
              disabled={!values.venue_country.trim()}
              onClick={() => {
                if (!values.venue_country.trim()) return;
                setStateOpen((prev) => !prev);
                setStateQuery('');
              }}
              className={`${inputClass} flex items-center justify-between text-left ${
                !values.venue_country.trim() ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
              }`}
              aria-haspopup="listbox"
              aria-expanded={stateOpen}
            >
              <span className={values.venue_state ? 'text-forest' : 'text-forest/40'}>
                {values.venue_state || (values.venue_country ? 'Select or search state / region' : 'Select country first')}
              </span>
              <ChevronDown className="h-4 w-4 text-moss shrink-0 transition-transform duration-200" />
            </button>

            {stateOpen && values.venue_country.trim() && (
              <div className="absolute z-30 mt-1 max-h-60 w-full overflow-hidden rounded-xl border border-nature-border bg-white shadow-xl">
                <div className="flex items-center border-b border-nature-border px-3 py-2 bg-plaster/50">
                  <Search className="h-4 w-4 text-moss shrink-0" />
                  <input
                    type="text"
                    value={stateQuery}
                    onChange={(e) => setStateQuery(e.target.value)}
                    placeholder="Type to search state / region…"
                    className="w-full bg-transparent px-2 text-sm text-forest placeholder:text-forest/40 focus:outline-none"
                    autoFocus
                  />
                  {stateQuery && (
                    <button
                      type="button"
                      onClick={() => setStateQuery('')}
                      className="text-moss hover:text-forest"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                <div className="max-h-48 overflow-y-auto p-1 text-sm">
                  {filteredStates.map((s) => {
                    const isSelected = values.venue_state.toLowerCase() === s.name.toLowerCase();
                    return (
                      <button
                        key={s.name}
                        type="button"
                        onClick={() => {
                          onChange({
                            venue_state: s.name,
                            venue_city: '',
                          });
                          setStateOpen(false);
                          setStateQuery('');
                        }}
                        className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-plaster transition-colors ${
                          isSelected ? 'font-semibold text-moss bg-plaster/80' : 'text-forest'
                        }`}
                      >
                        <span>{s.name}</span>
                        {isSelected && <Check className="h-4 w-4 text-moss" />}
                      </button>
                    );
                  })}

                  {filteredStates.length === 0 && (
                    <div className="p-3 text-center text-xs text-forest/60">
                      <p>
                        {availableStates.length === 0
                          ? `No predefined states for ${values.venue_country}.`
                          : `No state matches "${stateQuery}".`}
                      </p>
                      {stateQuery.trim() ? (
                        <button
                          type="button"
                          onClick={() => {
                            onChange({
                              venue_state: stateQuery.trim(),
                              venue_city: '',
                            });
                            setStateOpen(false);
                            setStateQuery('');
                          }}
                          className="mt-2 text-xs font-semibold text-moss underline"
                        >
                          Use &ldquo;{stateQuery.trim()}&rdquo; as custom state
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Field>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        {/* City: Autocomplete Input with suggestions */}
        <Field label="City" required error={cityError}>
          <div ref={cityRef} className="relative">
            <input
              type="text"
              data-field="venue_city"
              value={values.venue_city}
              onFocus={() => setCityOpen(true)}
              onChange={(e) => {
                onChange({ venue_city: e.target.value });
                setCityOpen(true);
              }}
              placeholder={values.venue_country ? 'Type city name for suggestions…' : 'Enter city'}
              className={inputClass}
            />

            {cityOpen && citySuggestions.length > 0 && (
              <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-xl border border-nature-border bg-white p-1 text-sm shadow-xl">
                <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-forest/50">
                  Suggested cities
                </div>
                {citySuggestions.map((city) => (
                  <button
                    key={city}
                    type="button"
                    onClick={() => {
                      onChange({ venue_city: city });
                      setCityOpen(false);
                    }}
                    className={`flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-left text-sm hover:bg-plaster transition-colors ${
                      values.venue_city.toLowerCase() === city.toLowerCase()
                        ? 'font-semibold text-moss bg-plaster/70'
                        : 'text-forest'
                    }`}
                  >
                    <span>{city}</span>
                    {values.venue_city.toLowerCase() === city.toLowerCase() && (
                      <Check className="h-3.5 w-3.5 text-moss" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </Field>

        {/* Postal Code */}
        <Field label="Postal code" error={postalError}>
          <input
            className={inputClass}
            data-field="venue_postal_code"
            value={values.venue_postal_code}
            onChange={(e) => onChange({ venue_postal_code: e.target.value })}
            placeholder="Optional postal / zip code"
          />
        </Field>
      </div>
    </>
  );
}
